import { useEffect, useRef } from "react";

import CopyFieldDropdownMenuItem from "components/CopyField/DropdownMenuItem";
import useApplyFilter from "components/Filters/useApplyFilter";
import ListEntitiesItem from "components/ListEntitiesItem";
import ListEntitiesItemDescription from "components/ListEntitiesItem/Description";
import ListEntitiesItemLink from "components/ListEntitiesItem/Link";
import MetaInfoListItem from "components/MetaInfoList/Item";
import PolicyTypeBadgeDropdown from "components/PolicyTypeBadge/Dropdown";
import TagsListFilterable from "components/TagsList/Filterable";
import Timestamp from "components/Timestamp";
import { Space } from "components/icons/generated";
import { PolicySuggestions, searchPolicySuggestionsDictionary } from "constants/policy";
import Box from "ds/components/Box";
import { useDrawerVisibilityForId } from "ds/components/DrawerNew/useDrawerVisibilityForId";
import DropdownMenuEllipsis from "ds/components/DropdownMenu/Ellipsis";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import useAnalytics from "hooks/useAnalytics";
import { AnalyticsPagePolicy } from "hooks/useAnalytics/pages/policy";
import useCanManagePolicy from "shared/Policy/access/useCanManagePolicy";
import { Policy } from "types/generated";
import { withTestId } from "utils/withTestId";

import { showDeleteConfirmation } from "../DeleteConfirmation";
import { COLUMN_GAP, COLUMN_ORDER, POLICIES_LIST_ITEM_TEST_ID } from "./constants";

type PoliciesListItemProps = {
  setRowHeight?: (size: number) => void;
  onEdit: () => void;
  hasAttachedEntities: boolean;
} & Pick<
  Policy,
  | "id"
  | "name"
  | "type"
  | "labels"
  | "createdAt"
  | "updatedAt"
  | "description"
  | "spaceDetails"
  | "plugin"
>;

const PoliciesListItem = ({
  id,
  name,
  type,
  labels,
  createdAt,
  updatedAt,
  spaceDetails,
  setRowHeight,
  onEdit,
  hasAttachedEntities,
  description,
  plugin,
}: PoliciesListItemProps) => {
  const rowRef = useRef<HTMLDivElement>(null);

  const handleRowHeight = () => {
    if (setRowHeight && rowRef.current) {
      setRowHeight(rowRef.current.getBoundingClientRect().height);
    }
  };

  const canManagePolicy = useCanManagePolicy()({
    space: spaceDetails.id,
    spaceDetails,
    plugin,
  });
  const showActionButtons = canManagePolicy.result || !!canManagePolicy.message;

  const isActive = useDrawerVisibilityForId(id);

  const { applyFilter, applySpaceFilter, applyLabelFilter } = useApplyFilter<PolicySuggestions>({
    searchSuggestionsDictionary: searchPolicySuggestionsDictionary,
  });

  // on every render, update the row height
  useEffect(handleRowHeight);

  const trackSegmentAnalyticsEvent = useAnalytics({
    page: AnalyticsPagePolicy.PoliciesList,
  });

  const handleDelete = () => {
    trackSegmentAnalyticsEvent("Start Delete");
    showDeleteConfirmation({
      id,
      hasAttachedEntities,
      name,
    }).then((finished) => {
      if (finished) trackSegmentAnalyticsEvent("Confirm Delete");
    });
  };

  return (
    <ListEntitiesItem
      ref={rowRef}
      direction="row"
      align="center"
      justify="between"
      grid
      gridTemplate={COLUMN_ORDER}
      gap={`0 ${COLUMN_GAP}`}
      isActive={isActive}
      {...withTestId(POLICIES_LIST_ITEM_TEST_ID)}
    >
      <Box direction="column" justify="center">
        <Box align="center">
          <ListEntitiesItemLink title={name} titleTag="h3" href={`/policy/${id}`} />
        </Box>

        {description && (
          <Box direction="row" align="start" fullWidth>
            <ListEntitiesItemDescription description={description} id={id} />
          </Box>
        )}

        <TagsListFilterable
          applyLabelFilter={applyLabelFilter(PolicySuggestions.Label)}
          applyFolderFilter={applyFilter(PolicySuggestions.Folder)}
          tags={labels}
          onExpand={handleRowHeight}
          withAutoAttach
        />
      </Box>

      <Box>
        <PolicyTypeBadgeDropdown type={type} applyFilter={applyFilter(PolicySuggestions.Type)} />
      </Box>

      <MetaInfoListItem
        icon={Space}
        linkText={spaceDetails.name}
        href={`/spaces/${spaceDetails.id}`}
        applyFilter={() => applySpaceFilter(PolicySuggestions.Space)(spaceDetails.id)}
        type={PolicySuggestions.Space}
      />

      <Box direction="row" align="center" shrink="0">
        <Timestamp timestamp={createdAt} variant="p-body3" />
      </Box>
      <Box direction="row" align="center" shrink="0">
        <Timestamp timestamp={updatedAt} variant="p-body3" />
      </Box>
      <DropdownMenuEllipsis tooltip="Policy actions">
        {showActionButtons && (
          <>
            <DropdownMenuItem
              tooltip={canManagePolicy.message}
              isDisabled={!canManagePolicy.result}
              onAction={onEdit}
            >
              Edit details
            </DropdownMenuItem>
          </>
        )}
        <CopyFieldDropdownMenuItem title="Copy policy ID" value={id} />
        {showActionButtons && (
          <DropdownMenuItem
            tooltip={canManagePolicy.message}
            isDisabled={!canManagePolicy.result}
            onAction={handleDelete}
            danger
          >
            Delete
          </DropdownMenuItem>
        )}
      </DropdownMenuEllipsis>
    </ListEntitiesItem>
  );
};

export default PoliciesListItem;
